/**
 * UImgUploadMode — mode upload direct de UImg (ha/447).
 *
 * Détaché de UImg pour clarifier la responsabilité : créer / cacher l'input
 * file natif, déclencher le picker, prévisualiser, POSTer le fichier au
 * dataRepo, dispatcher la réponse vers l'AppService.
 */

import { getAppService, getDataRepository, timeout, log } from "../../../../HA";
import type { Img } from "../Img";

const DEFAULT_UPLOAD_FIELD = "media";
const DEFAULT_STATIC_UPLOAD_FIELD = "file";

export interface UImgUploadHost {
    get(key: string): any;
    has(key: string): boolean;
    addToData(key: string, value: any): unknown;
    getElement(): Element;
    getImg(): Img;
    isStaticSource(): boolean;
    setResolvedSource(source: string): void;
}

export class UImgUploadMode {
    private pickerLocked = false;

    constructor(private readonly host: UImgUploadHost) {}

    public ensureFileInput(): HTMLInputElement {
        if (this.host.has("fileInput")) {
            return this.host.get("fileInput");
        }

        const input = document.createElement("input");
        input.type = "file";
        input.hidden = true;
        const accept = this.getAcceptedTypes();
        if (accept) {
            input.setAttribute("accept", accept);
        }
        input.addEventListener("change", () => {
            this.handleLocalFileSelection(input);
        });
        this.host.getElement().appendChild(input);
        this.host.addToData("fileInput", input);
        return input;
    }

    public openFilePicker(): void {
        if (this.pickerLocked) {
            return;
        }
        this.pickerLocked = true;
        const input = this.ensureFileInput();
        input.click();
        timeout(() => {
            this.pickerLocked = false;
        }, 300);
    }

    private handleLocalFileSelection(input: HTMLInputElement): void {
        const files = input.files;
        if (!files || files.length === 0) {
            return;
        }

        const file = files[0];
        this.previewFile(file);
        this.uploadFile(file);
        input.value = "";
    }

    private previewFile(file: File): void {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            if (!result) {
                return;
            }
            this.host.setResolvedSource(result);
        };
        reader.readAsDataURL(file);
    }

    public uploadFile(file: File): void {
        const form = new FormData();
        form.append(this.getUploadFieldName(), file);
        if (this.host.get("formid")) {
            form.append("formId", this.host.get("formid"));
        }

        const extra = this.host.get("uploadData");
        if (extra instanceof Map) {
            extra.forEach((value: any, key: string) => {
                if (value !== undefined && value !== null) {
                    form.append(key, value);
                }
            });
        }

        getDataRepository().saveForm(form).then((response: Map<any, any>) => {
            getAppService().processAction(response);
        }).catch((reason: any) => {
            log(["media upload error", reason]);
        });
    }

    public getUploadFieldName(): string {
        return this.host.get("uploadField")
            || (this.host.isStaticSource() ? DEFAULT_STATIC_UPLOAD_FIELD : DEFAULT_UPLOAD_FIELD);
    }

    private getAcceptedTypes(): string {
        return this.host.get("accept") || "";
    }
}

